import React, { useState, useEffect } from "react";
import {
  Bot,
  Star,
  Users,
  Award,
  ArrowRight,
  MessageSquare,
  ChevronDown,
  Play,
  Check,
  Shield,
  Clock,
  DollarSign,
} from "lucide-react";
import MainPageNavbar from "../../../components/common/MainPageNavbar";
import FeatureCard from "../../../components/common/FeatureCard";
import NetworkBackground from "../../../components/common/NetworkBackground";
import { useNavigate } from 'react-router-dom';
import { useLanguage } from "../../language/hooks/useLanguageHook";

// Import platform logos
import GoogleLogo from '../../../assets/images/google-color.png';
import YelpLogo from '../../../assets/images/Yelp-Emblem.png';
import TripAdvisorLogo from '../../../assets/images/tripadvisor-icon.png';
import OpenTableLogo from '../../../assets/images/Opentable.png';

// Import Sections
import HeroSection from './landing/HeroSection';
import FeaturesSection from './landing/FeaturesSection';
import ComparisonSection from './landing/ComparisonSection';
import IntegrationSection from './landing/IntegrationSection';
import StatsSection from './landing/StatsSection';
import ROICalculatorSection from './landing/ROICalculatorSection';
import DashboardPreviewSection from './landing/DashboardPreviewSection';
import TestimonialsSection from './landing/TestimonialsSection';
import FinalCtaSection from './landing/FinalCtaSection';

// Global styles moved to a CSS file or Tailwind config ideally,
// but kept here for simplicity in this refactoring step if not done separately.
const styles = `
  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.95);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  .feature-card {
    opacity: 0;
    animation: fadeInScale 0.5s ease-out forwards;
  }

  /* Add scroll animation base styles if not in global CSS */
  .scroll-fade-in, .scroll-slide-in, .scroll-scale-in {
    opacity: 0;
    transition: opacity 0.6s ease-out, transform 0.6s ease-out;
  }
  .scroll-fade-in.visible, .scroll-slide-in.visible, .scroll-scale-in.visible {
    opacity: 1;
  }
  .scroll-slide-in {
    transform: translateY(20px);
  }
  .scroll-slide-in.visible {
    transform: translateY(0);
  }
  .scroll-scale-in {
     transform: scale(0.95);
  }
  .scroll-scale-in.visible {
     transform: scale(1);
  }
`;

const getFeaturesList = (t, cardKey) => {
  const features = t(`public.landing.features.cards.${cardKey}.features`, { returnObjects: true });
  return Array.isArray(features) ? features : [];
};

const LandingPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  
  // Add ROI calculator state
  const [roiInputs, setRoiInputs] = useState({
    monthlyReviews: 100,
    responseTime: 24,
    staffCost: 25
  });

  // Calculate ROI metrics
  const calculateROI = () => {
    const { monthlyReviews, responseTime, staffCost } = roiInputs;

    // If any inputs are invalid or negative, return zeros
    if (responseTime <= 0 || isNaN(responseTime)) {
      return {
        timeSaved: "0",
        costSaved: "0",
        responseImprovement: "0"
      };
    }

    // Calculate metrics
    const timePerReview = responseTime / 60; // Convert to hours
    const totalTimeSaved = Math.round(monthlyReviews * timePerReview * 0.75); // 75% time reduction
    const monthlyCostSaved = Math.round(totalTimeSaved * staffCost);
    const responseImprovement = Math.round((responseTime - (responseTime * 0.25)) / responseTime * 100);

    return {
      timeSaved: totalTimeSaved.toString(),
      costSaved: monthlyCostSaved.toString(),
      responseImprovement: responseImprovement.toString()
    };
  };

  const handleInputChange = (field, value) => {
    // Remove non-numeric characters except decimal point
    const numericValue = value.replace(/[^\d.]/g, '');
    
    // Additional validation
    let finalValue = numericValue;
    
    // Prevent invalid numbers
    if (isNaN(Number(finalValue))) {
      finalValue = '0';
    }
    
    // For all fields, allow any positive number including zero
    if (Number(finalValue) < 0) {
      finalValue = '0';
    }
    
    setRoiInputs(prev => ({
      ...prev,
      [field]: finalValue
    }));
  };

  const roi = calculateROI();

  // Add scroll animation logic
  useEffect(() => {
    const observerCallback = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
        // Optional: Remove class if not intersecting for re-animation on scroll up
        // else {
        //   entry.target.classList.remove('visible');
        // }
      });
    };

    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1 // Adjust threshold as needed
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);
    const animatedElements = document.querySelectorAll('.scroll-fade-in, .scroll-slide-in, .scroll-scale-in');

    animatedElements.forEach(el => observer.observe(el));

    // Cleanup function
    return () => animatedElements.forEach(el => observer.unobserve(el));
  }, []); // Empty dependency array ensures this runs only once on mount

  const testimonials = [
    {
      text: "FeedBacked transformed how we handle customer feedback. Our response time improved by 300%!",
      author: "Sarah Chen",
      role: "Owner, The Golden Plate",
      image: "/api/placeholder/64/64",
    },
    {
      text: "The AI-powered responses are incredibly personalized. Our customers feel heard and valued.",
      author: "Michael Rodriguez",
      role: "Manager, Bistro 54",
      image: "/api/placeholder/64/64",
    },
    {
      text: "The analytics helped us identify and fix key issues in our service. 5-star ratings up by 45%!",
      author: "Emma Thompson",
      role: "Director, Fine Dining Co",
      image: "/api/placeholder/64/64",
    },
  ];

  const StatCard = ({ value, label, sublabel }) => (
    <div className="transform hover:scale-105 transition-transform">
      <div className="text-5xl font-bold mb-2">{value}</div>
      <div className="text-xl mb-1">{label}</div>
      <div className="text-blue-200 text-sm">{sublabel}</div>
    </div>
  );

  const handleStartFreeTrial = () => {
    navigate('/onboarding');
  };

  const trustedBy = [
    '/logos/restaurant1.png',
    '/logos/restaurant2.png',
    '/logos/restaurant3.png',
    '/logos/restaurant4.png',
    '/logos/restaurant5.png',
  ];

  const featureCards = [
    {
      icon: MessageSquare,
      title: t("public.landing.features.cards.responses.title"),
      description: t("public.landing.features.cards.responses.description"),
      highlight: t("public.landing.features.cards.responses.highlight"),
      features: getFeaturesList(t, "responses")
    },
    {
      icon: Users,
      title: t("public.landing.features.cards.performance.title"),
      description: t("public.landing.features.cards.performance.description"),
      highlight: t("public.landing.features.cards.performance.highlight"),
      features: getFeaturesList(t, "performance")
    },
    {
      icon: Star,
      title: t("public.landing.features.cards.intelligence.title"),
      description: t("public.landing.features.cards.intelligence.description"),
      highlight: t("public.landing.features.cards.intelligence.highlight"),
      features: getFeaturesList(t, "intelligence")
    }
  ];


  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Apply global styles */}
      <style dangerouslySetInnerHTML={{ __html: styles }} />

      {/* Background Container */}
      <div className="absolute inset-0 -z-10"> {/* Ensure background is behind content */}
        <NetworkBackground
          color="rgba(37, 99, 235, 0.3)"
          pointColor="rgba(37, 99, 235, 0.7)"
          pointCount={50}
          maxDistance={500}
          speedFactor={0.2}
          backgroundColor="rgb(243, 244, 255)"
          pointSize={2}
          lineWidth={1}
          className="!fixed" // Ensure it stays fixed
        />
        {/* Enhanced Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/50 to-white/90" />
      </div>


      {/* Sections */}
      <div className="relative"> {/* Add relative positioning for z-index context */}
        <HeroSection
          onStartFreeTrial={handleStartFreeTrial}
          onOpenVideoModal={() => setIsVideoModalOpen(true)}
        />
        {/* Add TrustedBy section here if separated */}
        <FeaturesSection />
        <ComparisonSection />
        <IntegrationSection />
        <StatsSection />
        <ROICalculatorSection onStartFreeTrial={handleStartFreeTrial} />
        <DashboardPreviewSection />
        <TestimonialsSection />
        <FinalCtaSection
          onStartFreeTrial={handleStartFreeTrial}
          onOpenVideoModal={() => setIsVideoModalOpen(true)}
        />
      </div>

      {/* Video Modal (Keep state management here or lift if needed elsewhere) */}
      {isVideoModalOpen && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-white p-4 rounded-lg relative max-w-3xl w-full">
            <button
              onClick={() => setIsVideoModalOpen(false)}
              className="absolute -top-4 -right-4 bg-white rounded-full p-1 text-gray-700 hover:text-black"
            >
              &times; {/* Simple close icon */}
            </button>
            {/* Replace with actual video player */}
            <div className="aspect-video bg-gray-300 flex items-center justify-center">
              <p>Video Player Placeholder</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
